import {
    CONSORS_SUBSCRIPTION_STATUSES,
    EUROPACE_PROCESS_PLATFORMS,
    EUROPACE_PROCESS_STATUSES,
    LEAD_ATTRIBUTION_STATUSES,
    LOAN_REQUEST_ALL_BANKS,
    LOAN_REQUEST_CLASSIFICATION,
    LOAN_REQUEST_TAGS,
    LOANREQUEST_BUCKETS,
    LOANREQUEST_STATUSES,
    PARTNER_LEVELS,
    SUB_STATUSES,
} from 'cpool-constants';
import {
    LoanRequest_ApplicantIndexed,
    LoanRequest_Classified,
    LoanRequest_Created,
    LoanRequest_LegitimationChanged,
} from 'cpool-events';
import {
    schemaId,
    schemaLoanRequestAcceptedOffers,
    schemaTimestamp,
    schemaTracking,
    schemaUserCategories,
    teamCategory,
} from 'cpool-schemas';
import { z } from 'zod';

// export type LoanRequestsTableSchema = {
//     applicant1SchufaScore: number | null;
//     applicant2SchufaScore: number | null;
//     partnerLead: boolean | null;
//     assignment: ValueOf<typeof LOAN_REQUEST_CLASSIFICATION> | null;
//     consors: {
//         status: typeof CONSORS_SUBSCRIPTION_STATUSES;
//         subscriptionIdentifier: string;
//         timestamp: number;
//         warning?: Record<string, Array<string>>;
//     } | null;
//     createdAt: Date;
//     data: EventSchema<LoanRequest_Created> & {
//         antragsteller1: EventSchema<LoanRequest_ApplicantIndexed>['applicant'];
//         antragsteller2?: EventSchema<LoanRequest_ApplicantIndexed>['applicant'];
//         applicants: Array<string>;
//         consors?: {
//             status: typeof CONSORS_SUBSCRIPTION_STATUSES;
//             subscriptionIdentifier: string;
//             timestamp: number;
//             warning?: Record<string, Array<string>>;
//         } | null;
//         legitimation: EventSchema<LoanRequest_LegitimationChanged>['legitimation'];
//         status: EventSchema<LoanRequest_StatusChanged>['status'];
//         statusReason: EventSchema<LoanRequest_StatusChanged>['statusReason'];
//         type: EventSchema<LoanRequest_Classified>['type'];
//     };
//
//     europace: {
//         status: EUROPACE_PROCESS_STATUS;
//         id: string | null;
//         timestamp: number | null;
//         warning: string | null;
//     } | null;
//     filters: Array<LOAN_REQUEST_TAG>;
//     id: string;
//     lastOfferRequestId: string | null;
//     resubmission: number | null;
//     userIds: string[];
//     activeUserId: string | null;
// };

export const LoanRequestsTableDefinitionZod = LoanRequest_Created.prototype.getSchema().merge(
    z.object({
        acceptedOffers: schemaLoanRequestAcceptedOffers,
        activeUserId: schemaId.nullable(),
        activeUserSet: z.object({ setAt: schemaTimestamp, setBy: schemaId, userId: schemaId }).nullish(),
        // previousUserId: set when the previous added advisor handed their share on (KC-5322)
        addedAssignedUser: z
            .object({
                addedAt: schemaTimestamp,
                addedBy: schemaId,
                previousUserId: schemaId.nullish(),
                userId: schemaId,
            })
            .nullish(),
        // this will only be null if the applicant is not yet indexed
        antragsteller1: LoanRequest_ApplicantIndexed.prototype.getSchema().shape.applicant.nullable(),
        antragsteller2: LoanRequest_ApplicantIndexed.prototype.getSchema().shape.applicant.nullable(),
        applicant1SchufaScore: z.number().nullable(),
        applicant2SchufaScore: z.number().nullable(),
        applicants: z.array(schemaId).default([]),
        assignment: z.nativeEnum(LOAN_REQUEST_CLASSIFICATION).nullable(),
        attributionState: z.nativeEnum(LEAD_ATTRIBUTION_STATUSES).nullable(),
        bucketHistory: z
            .array(
                z.object({
                    bucket: z.nativeEnum(LOANREQUEST_BUCKETS),
                    changedAt: z.coerce.date(),
                    selectedBy: schemaId.nullable(),
                }),
            )
            .default([]),
        coApplicantHidden: z.boolean().default(false),
        consors: z
            .object({
                status: z.nativeEnum(CONSORS_SUBSCRIPTION_STATUSES),
                subscriptionIdentifier: z.string(),
                timestamp: z.number(),
                warning: z.record(z.array(z.string())).optional(),
            })
            .nullable(),
        createdAt: z.coerce.date(),
        dsgvo: z
            .object({ isCensored: z.boolean().default(false), isCensorPending: z.boolean().default(false) })
            .default({ isCensored: false, isCensorPending: false }),
        europace: z
            .object({
                id: z.string().nullable(),
                platform: z.nativeEnum(EUROPACE_PROCESS_PLATFORMS),
                status: z.nativeEnum(EUROPACE_PROCESS_STATUSES),
                timestamp: z.number().nullable(),
                warning: z.string().nullable(),
            })
            .nullable(),
        filters: z.array(
            // This needs to be loosely typed to allow for future tags (unknown banks, etc.)
            z.nativeEnum({ ...LOAN_REQUEST_TAGS, ...LOAN_REQUEST_ALL_BANKS, ...LOANREQUEST_BUCKETS }).or(z.string()),
        ),
        id: schemaId,
        ikCategory: teamCategory.optional(),
        lastOfferRequestId: schemaId.nullable(),
        // this is nullable until the LoanRequest_LegitimationChanged event is published
        legitimation: LoanRequest_LegitimationChanged.prototype.getSchema().shape.legitimation.nullable(),
        partnerLead: z.boolean().nullable(),
        partnerLevel: z.array(z.union([z.nativeEnum(PARTNER_LEVELS), z.string()])).nullable(),
        resubmission: z.number().nullable(),
        // When the current resubmission was set (epoch millis). Nullish: absent on rows created before this
        // field existed and on rows without a resubmission.
        resubmissionCreatedAt: z.number().nullish(),
        slotCategories: schemaUserCategories.optional(),
        status: z.nativeEnum(LOANREQUEST_STATUSES),
        statusReason: z.array(z.string()).nullable(),
        subStatus: z.nativeEnum(SUB_STATUSES).nullable(),
        tracking: schemaTracking,
        type: LoanRequest_Classified.prototype.getSchema().shape.type.nullable(), // this is nullable until the loan request is classified
        userIds: z.array(schemaId),
    }),
);

export type LoanRequestsTableSchema = z.infer<typeof LoanRequestsTableDefinitionZod>;
